import {
  CustomerInvitationListPaginatedResponseDto,
  CustomerInvitationListResponseDto,
} from "@/lib/api/interfaces/users.interface";
import { PaginatedInfo } from "@/lib/api/interfaces/utils";
import {
  useUsersCustomerDeleteInvitation,
  useUsersCustomerFindInvitations,
  useUsersCustomerResendInvitation,
  UseUsersCustomerFindInvitationsParams,
} from "@/lib/api/requests/users.requests";
import { formatDate } from "@/lib/utils/date-formatter";
import { Button } from "@/ui-components/ui/button";
import ConfirmationDialog from "@/ui-components/ui/confirmation-dialog";
import { SuspenseWrapper } from "@/ui-components/ui/suspense-wrapper";
import {
  DataTable2,
  DataTable2ColumnHeader,
  DataTable2Pagination,
  DataTable2Toolbar,
  useDataTable2,
} from "@/util-components/data-table-2/components";
import { SubPageLayout } from "@/util-components/page-layout/sub-page-layout";
import { UseMutateFunction } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { Plus, UserPlus, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "@/lib/toast";
import CreateCustomerInvitationDialog from "../create-dialog";

const CustomerUsersInvitationsWrapper = () => {
  const navigate = useNavigate();
  return (
    <SubPageLayout
      pageTitle="Einladungen"
      parentPageTitle="Mitglieder & Rollen"
      parentPageCallback={() => navigate("/users")}
      navigateBackCallback={() => navigate("/users")}
      icon={<UserPlus className="h-6 w-6" />}
    >
      <SuspenseWrapper type="green" text="Lädt Einladungen...">
        <CustomerUsersInvitations />
      </SuspenseWrapper>
    </SubPageLayout>
  );
};

const getColumns = (
  deleteInvitation: UseMutateFunction<any, Error, { id: string }, unknown>,
  resendInvitation: UseMutateFunction<any, Error, { id: string }, unknown>
): ColumnDef<CustomerInvitationListResponseDto>[] => {
  return [
    {
      accessorKey: "firstName",
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Name" />,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div>
            <p>
              {row.original.firstName} {row.original.lastName}
            </p>
            <p className="text-sm text-gray-500">{row.original.mail}</p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Erstellt am" />,
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      accessorKey: "expiresAt",
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Ablaufdatum" />,
      cell: ({ row }) => (row.original.expiresAt ? formatDate(row.original.expiresAt) : "Nicht festgelegt"),
    },
    {
      id: "actions",
      header: "Aktionen",
      cell: ({ row }) => {
        const onDelete = () => {
          deleteInvitation({ id: row.original._id });
        };

        const onResend = () => {
          resendInvitation({ id: row.original._id });
        };

        return (
          <div className="flex gap-2 justify-end">
            <ConfirmationDialog
              title="Einladung erneut senden"
              onConfirm={onResend}
              description="Möchtest du diese Einladung wirklich erneut per E-Mail versenden?"
            >
              <Button variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Erneut senden
              </Button>
            </ConfirmationDialog>
            <ConfirmationDialog
              title="Einladung löschen"
              onConfirm={onDelete}
              description="Möchtest du diese Einladung wirklich unwiderruflich löschen?"
            >
              <Button variant="destructive" size="sm">
                Löschen
              </Button>
            </ConfirmationDialog>
          </div>
        );
      },
    },
  ];
};

const CustomerUsersInvitations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number.parseInt(searchParams.get("page") ?? "1", 10);
  const [shouldReload, setShouldReload] = useState<{ shouldReload: boolean; refetch: boolean }>({
    shouldReload: false,
    refetch: false,
  });
  const [users, setUsers] = useState<CustomerInvitationListResponseDto[]>();
  const [paginatedInfo, setPaginatedInfo] = useState<PaginatedInfo>();
  const [params, setParams] = useState<UseUsersCustomerFindInvitationsParams>({
    limit: 10,
    page: Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1,
  });

  const [createInvitationDialogOpen, setCreateInvitationDialogOpen] = useState(false);
  const { mutate: deleteInvitation } = useUsersCustomerDeleteInvitation();
  const { mutate: resendInvitation } = useUsersCustomerResendInvitation();

  const onDataLoaded = (data: CustomerInvitationListPaginatedResponseDto) => {
    setUsers(data.docs);
    setPaginatedInfo(data);
    setShouldReload({ shouldReload: false, refetch: false });
  };

  useEffect(() => {
    setShouldReload({ shouldReload: true, refetch: false });
    setSearchParams(
      {
        page: (params.page ?? 1).toString(),
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const columns = getColumns(
    (id) => {
      deleteInvitation(id, {
        onSuccess: () => {
          toast.success("Einladung erfolgreich gelöscht");
          setShouldReload({ shouldReload: true, refetch: true });
        },
        onError: (error) => {
          toast.error("Einladung konnte nicht gelöscht werden", { error });
        },
      });
    },
    (id) => {
      resendInvitation(id, {
        onSuccess: () => {
          toast.success("Einladung erfolgreich erneut gesendet");
        },
        onError: (error) => {
          toast.error("Einladung konnte nicht gesendet werden", { error });
        },
      });
    }
  );

  const { table } = useDataTable2({
    data: users || [],
    columns,
    getRowId: (row) => row._id,
  });

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <SuspenseWrapper type="green" text="Lädt Nutzer...">
        {shouldReload.shouldReload && (
          <CustomerUsersInvitationsFind
            onDataLoaded={onDataLoaded}
            params={params}
            shouldRefetch={shouldReload.refetch}
          />
        )}
      </SuspenseWrapper>

      <div className="bg-white overflow-hidden">
        <DataTable2Toolbar
          className="px-6"
          table={table}
          columnTogglePosition="left"
          right={
            <Button size="sm" className="h-10 rounded-xl" onClick={() => setCreateInvitationDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nutzer einladen
            </Button>
          }
        />

        <div className="px-6 pb-4 pt-4">
          <DataTable2 table={table} />
        </div>

        <div className="border-t border-gray-100 px-6 py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-gray-500" />
            {paginatedInfo ? (
              <DataTable2Pagination
                currentPage={params.page ?? 1}
                pageCount={paginatedInfo.pageCount ?? 0}
                hasNextPage={paginatedInfo.hasNextPage ?? false}
                hasPrevPage={paginatedInfo.hasPrevPage ?? false}
                onPageChange={(page) => setParams({ ...params, page })}
                pageSize={params.limit ?? 10}
                onPageSizeChange={(limit) => setParams({ ...params, limit, page: 1 })}
              />
            ) : null}
          </div>
        </div>
      </div>

      <SuspenseWrapper type="green" text="Öffnet Dialog...">
        {createInvitationDialogOpen && (
          <CreateCustomerInvitationDialog
            open={createInvitationDialogOpen}
            onClose={(refetchData) => {
              setCreateInvitationDialogOpen(false);
              if (refetchData) {
                setShouldReload({ shouldReload: true, refetch: true });
              }
            }}
          />
        )}
      </SuspenseWrapper>
    </div>
  );
};

const CustomerUsersInvitationsFind = ({
  onDataLoaded,
  params,
  shouldRefetch,
}: {
  onDataLoaded: (data: CustomerInvitationListPaginatedResponseDto) => void;
  params: UseUsersCustomerFindInvitationsParams;
  shouldRefetch: boolean;
}) => {
  const { data: response, refetch } = useUsersCustomerFindInvitations(params);

  const refetchData = useCallback(async () => {
    const { data } = await refetch();
    if (data) {
      onDataLoaded(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch]);

  useEffect(() => {
    if (response) {
      onDataLoaded(response);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, onDataLoaded]);

  useEffect(() => {
    if (shouldRefetch) {
      refetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefetch]);

  return null;
};

export default CustomerUsersInvitationsWrapper;
